import type { ProductCardTag } from '@src/components/product-card/product-card.types'
import type { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import productBuyNowStateManager from '@src/quince-site-common/src/redux/globalStateManager/ProductBuyNowStateManager'
import type { SvdNativeCardVariant, SvdNativeProduct } from '@src/types/svd-product-card.types'

import { fireSvdQuickAdd } from '../svd-product-card.analytics'

import { buildSvdQuickAddPayload } from './svd-to-product-card-shim'

export interface OpenSvdQuickAddArgs {
  product: SvdNativeProduct
  selectedVariant?: SvdNativeCardVariant
  /** Applicable tags for the selected variant — feeds the `oos_flag` in the click event. */
  applicableTags: ProductCardTag[]
  trackConfig?: TrackConfig
  productPosition?: number
  searchQuery?: string
  /** Escape hatch (reco/cart surfaces pass their own handler instead of opening the modal). */
  override?: () => void
}

/**
 * The single quick-add trigger for the SVD card. Both surfaces — the grid-card hover bag icon and the
 * wishlist "add to bag" button-underneath — call this, so behavior stays identical and the Path-A→B
 * swap is isolated to the modal router (`product-buy-now-ds/index.tsx`).
 *
 * Path A (today): opens the existing enrich-based `ProductBuyNow` modal via the ids-only shim; the
 * modal enriches by `spid` and renders the shared `ProductFormWrapper`. `svdCardToken` is carried for
 * Path B (SVD-native buy box) and ignored today. Fires `click_quick_shop` analytics first (parity
 * with the legacy card — it tracks even for zero-price), then guards the modal open.
 */
export function openSvdQuickAdd({
  product,
  selectedVariant,
  applicableTags,
  trackConfig,
  productPosition,
  searchQuery,
  override,
}: OpenSvdQuickAddArgs) {
  fireSvdQuickAdd({ product, selectedVariant, applicableTags, trackConfig, productPosition, searchQuery })

  // Zero-price products don't open the modal (parity with the legacy card's guard).
  if (!selectedVariant?.price.salePrice) return

  if (override) {
    override()
    return
  }

  const { productCard, selectedOptions, svdCardToken } = buildSvdQuickAddPayload(product, selectedVariant)
  productBuyNowStateManager.showBuyNowPopUp({
    productCard,
    selectedOptions,
    svdCardToken,
    productClickTrackConfig: trackConfig,
    position: productPosition,
    searchQuery,
  })
}
